import { GLOBAL_ROLE_VALUES } from '@napaicp/shared'
import { z } from 'zod'
import {
  displayNameSchema,
  emailSchema,
  ianaTimeZoneSchema,
  localeSchema,
  nullableUrlSchema,
  organizationDisplayNameSchema,
  publicIdSchema,
  utcTimestampSchema,
} from './primitives'

export const USER_STATUS_VALUES = ['active', 'suspended', 'deleted'] as const
export const userStatusSchema = z.enum(USER_STATUS_VALUES)
export type UserStatus = z.infer<typeof userStatusSchema>

/**
 * The account as the platform stores it. `global_role` here is the platform
 * role only; competition roles are never denormalised onto this record.
 */
export const userSchema = z.object({
  id: publicIdSchema,
  email: emailSchema,
  displayName: displayNameSchema,
  avatarUrl: nullableUrlSchema,
  globalRole: z.enum(GLOBAL_ROLE_VALUES),
  status: userStatusSchema,
  locale: localeSchema,
  timezone: ianaTimeZoneSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
  lastLoginAt: utcTimestampSchema.nullable(),
  /**
   * Set once, the first time the account's email is confirmed (verification
   * link followed, or a provider that vouches for it at creation). Never
   * cleared. This is the authoritative fact the Initial Super Admin Bootstrap
   * flow (PR-024, API-039) checks — see migration 0014.
   */
  emailVerifiedAt: utcTimestampSchema.nullable(),
  /**
   * DM-026/028, DATA-034 (Wave R). True only while the account is using a
   * Temporary Initial Password and has not yet completed its forced
   * first-login password change. Read by the session/authorization
   * middleware on every request — see `middleware/authorize.ts`.
   */
  passwordChangeRequired: z.boolean(),
  /**
   * DM-030/031, DATA-036, GOV-028, BR-093 (Wave T). Self-declared only —
   * never treated anywhere as a verified Organization Membership,
   * Competition Organizer identity, or Platform Trust signal.
   */
  organizationDisplayName: organizationDisplayNameSchema.nullable(),
})

export type User = z.infer<typeof userSchema>

/**
 * What `/api/auth/session` returns about the caller. Deliberately excludes
 * anything that only the platform needs (status transitions, timestamps other
 * than the login time).
 */
export const currentUserDtoSchema = z.object({
  id: publicIdSchema,
  email: emailSchema,
  displayName: displayNameSchema,
  avatarUrl: nullableUrlSchema,
  globalRole: z.enum(GLOBAL_ROLE_VALUES),
  locale: localeSchema,
  timezone: ianaTimeZoneSchema,
  /** UX-027: the frontend must route straight to the forced password-change screen while this is true. */
  passwordChangeRequired: z.boolean(),
})

export type CurrentUserDto = z.infer<typeof currentUserDtoSchema>

/**
 * What one user is allowed to learn about another. Notably no email: in an
 * anonymous competition an email address is an identity leak.
 */
export const userSummaryDtoSchema = z.object({
  id: publicIdSchema,
  displayName: displayNameSchema,
  avatarUrl: nullableUrlSchema,
})

export type UserSummaryDto = z.infer<typeof userSummaryDtoSchema>

export function toCurrentUserDto(user: User): CurrentUserDto {
  return {
    id: user.id,
    email: user.email,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
    globalRole: user.globalRole,
    locale: user.locale,
    timezone: user.timezone,
    passwordChangeRequired: user.passwordChangeRequired,
  }
}

export function toUserSummaryDto(user: User): UserSummaryDto {
  return {
    id: user.id,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
  }
}
